import React from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import platform from '@lyearn/core/utils/Platform';
import { useTranslation } from '@lyearn/i18n';
import { Logo } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import { Image } from '@/components/Image';
import { Link } from '@/components/Link';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { useAppConfig } from '@/hooks/useAppConfig';

import { getLogoSize, logoStyles } from './styles';
import type { LeftNodeProps } from './types';

function DefaultLeftNode(props: LeftNodeProps) {
  const { t } = useTranslation();
  const { textColor, logoPreviewUrl } = props;
  const {
    [FeatureFlags.EnableUnifiedDashboard]: isUnifiedDashboardEnabled,
    [FeatureFlags.ShowUserProfile]: showUserProfile,
    [FeatureFlags.DefaultRestricted]: isDefaultRestricted,
  } = useFeatureFlags();
  const {
    site: { logoUrl, favicon, appUrl, appUrlTarget, name, isUserOnboardingAiEnabled },
  } = useAppConfig();
  const { isMobile } = useScreen();

  if (!showUserProfile) return null;

  const logoSize = getLogoSize({
    isUserOnboardingAiEnabled,
    isUnifiedDashboardEnabled,
    isDefaultRestricted,
  });

  const LogoImage = () => {
    if (isMobile && favicon) {
      return (
        <Image
          useImg
          alt={t('Common:LogoOfImage', { imageAlt: name })}
          className="h-32 w-32 rounded-8"
          resizeMode="contain"
          src={favicon}
        />
      );
    }

    return logoUrl ? (
      <Image
        useImg
        alt={t('Common:LogoOfImage', { imageAlt: name })}
        className={cn(logoSize.maxWidth, logoSize.height)}
        resizeMode="contain"
        src={logoPreviewUrl || logoUrl}
        // need style prop to work in native
        style={
          platform.OS !== 'web' ? { height: 24, width: 100, alignItems: 'flex-start' } : undefined
        }
      />
    ) : (
      <Logo
        className={cn(
          'transition duration-300 ease-in-out',
          textColor,
          logoStyles,
          logoSize.height,
        )}
        fill="currentColor"
      />
    );
  };

  const LogoComponent = appUrl ? (
    <a
      aria-label={t('Common:GoToHome', { appName: name })}
      href={appUrl}
      rel="noreferrer"
      target={appUrlTarget}>
      <LogoImage />
    </a>
  ) : (
    <Link aria-label={t('Common:GoToHome', { appName: name })} to="/">
      <LogoImage />
    </Link>
  );

  return (
    <Box
      aria-label={t('Common:Home')}
      className={cn(isUnifiedDashboardEnabled ? '-ms-6 xs:ms-6' : 'ps-24 xs:ps-24')}
      component="nav">
      {LogoComponent}
    </Box>
  );
}

export default React.memo(DefaultLeftNode);
